import { useCallback } from 'react';
import { CheckboxElement, TextFieldElement, useFormContext } from 'react-hook-form-mui';

import { Box, CardContent, useTheme } from '@mui/material';
import { makeStyles } from 'makeStyles';

import { Comment } from 'commonComponents/Comment';
import { MaxVersion } from 'commonComponents/MaxVersion';

import { AdditionalLicenseData } from './AdditionalLicenseData';
import { ApplicationNames } from './ApplicationNames';
import { CanSignFiles } from './CanSignFiles';
import { DurationWithUnits } from './DurationWithUnit';
import { HardwareBinding } from './HardwareBinding';
import { Internal } from './Internal';
import { LicenseFeatureTreeView } from './LicenseFeatureTreeView';
import { MaximumNumberOfConcurrentUsersField } from './MaximumNumberOfConcurrentUsersField';

const useStyles = makeStyles()(theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: theme.spacing(1),
        rowGap: theme.spacing(2),
        '& > *': {
            flex: '400px',
        },
    },
}));

export function ConfigurationFormContents({
    isAdhocConfiguration = false,
    disabled = false,
    availableFeatures,
    namespace = '',
    showInternal = false,
    showMaxConcurrent = false,
}) {
    const { classes } = useStyles();
    const theme = useTheme();
    const formContext = useFormContext();
    const { register } = formContext;

    const name = useCallback(fieldName => `${namespace}${fieldName}`, [namespace]);
    return (
        <CardContent data-testid="ConfigurationForm_CardContent">
            <Box className={classes.container}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(2),
                    }}
                >
                    {!isAdhocConfiguration ? (
                        <TextFieldElement
                            name={name('name')}
                            label="Configuration Name"
                            id={name('name')}
                            required
                            disabled={disabled}
                            autoComplete="off"
                            variant="outlined"
                        />
                    ) : (
                        <input type="hidden" {...register(name('name'), { required: false })} />
                    )}
                    <ApplicationNames name={name('runtimeFeatures')} disabled={disabled} />
                    <TextFieldElement
                        label="Part No."
                        name={name('partNo')}
                        autoComplete="off"
                        variant="outlined"
                        disabled={disabled}
                    />
                    <DurationWithUnits
                        name={name('validity')}
                        durationLabel="Validity (Empty for Unlimited Validity)"
                        unitLabel="Validity Unit"
                        disabled={disabled}
                    />
                    <DurationWithUnits
                        name={name('supportValidity')}
                        durationLabel="Support Period (Empty for Unlimited Support)"
                        unitLabel="Support Period Unit"
                        disabled={disabled}
                    />
                    <TextFieldElement
                        label="Maximum Number of Activations (Empty or 0 for Unlimited Activations)"
                        name={name('maximumActivations')}
                        placeholder="unlimited"
                        type="number"
                        inputProps={{ min: '0', step: '1' }}
                        InputLabelProps={{ shrink: true }}
                        disabled={disabled}
                        autoComplete="off"
                        variant="outlined"
                    />
                    {showMaxConcurrent && (
                        <MaximumNumberOfConcurrentUsersField
                            name={name('maxNumberOfConcurrentUsers')}
                            disabled={disabled}
                            required={false}
                        />
                    )}
                    <HardwareBinding name={name('hardwareBinding')} form={formContext} disabled={disabled} />
                    <MaxVersion disabled={disabled} autoComplete="off" variant="outlined" name={name('max_version')} />
                    <CheckboxElement name={name('hasSerialNumber')} label="Include Serial Number" disabled={disabled} />
                    <Internal name={name('internal')} disabled={disabled} showInternal={showInternal} />
                    <CanSignFiles
                        name={name('can_sign_files')}
                        hardwareBindingName={name('hardwareBinding')}
                        disabled={disabled}
                    />
                    <Comment name={name('comment')} autoComplete="off" variant="outlined" disabled={disabled} />
                    <AdditionalLicenseData name={name('additionalLicenseData')} disabled={disabled} />
                </Box>
                <LicenseFeatureTreeView
                    name={name('features')}
                    availableFeatures={availableFeatures}
                    form={formContext}
                    disabled={disabled}
                    required={!disabled}
                />
            </Box>
        </CardContent>
    );
}
